/**
 * Forgot Password Page
 *
 * Request a password reset email.
 */

import {
  Alert,
  AlertDescription,
  AuthTemplate,
  Button,
  Form,
  Input,
  Label,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
        return;
      }

      setSuccess(true);
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthTemplate title="Check your email" centered>
        <Text color="secondary" align="center">
          We&apos;ve sent a password reset link to <strong>{email}</strong>
        </Text>
        <Text color="secondary" size="sm" align="center">
          Didn&apos;t receive the email? Check your spam folder or{' '}
          <Button
            variant="link"
            onClick={() => setSuccess(false)}
            className="h-auto p-0"
          >
            try again
          </Button>
        </Text>

        <Link to="/login" className="text-primary inline-block hover:underline">
          Back to sign in
        </Link>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate
      title="Reset your password"
      subtitle="Enter your email address and we'll send you a link to reset your password."
    >
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className="mt-8" gap="lg">
        <Stack gap="xs">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />
        </Stack>

        <Button type="submit" loading={isLoading} className="w-full">
          Send reset link
        </Button>
      </Form>

      <Stack align="center">
        <Link
          to="/login"
          className="text-foreground/70 hover:text-foreground text-base"
        >
          Back to sign in
        </Link>
      </Stack>
    </AuthTemplate>
  );
}
